import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { formatApiError } from "../lib/api";
import { createCase, listMyCases, getMyCase, replyToMyCase, submitCsat, CASE_QUEUES } from "../lib/supportCases";

const STATUS_LABEL = {
  new: "New", open: "Open", waiting_customer: "Waiting on you",
  waiting_confirmph: "With ConfirmPH", resolved: "Resolved", closed: "Closed",
};

/** Requester-facing support-case UI. Mounted at both /portal/support
 * (renters) and /app/help (business staff): the backend endpoint reads
 * the caller's own token to decide requester_type, so this component
 * doesn't need to know which context it's in. */
export default function RequesterSupport() {
  const [caseId, setCaseId] = useState(null);
  if (caseId) return <CaseThread caseId={caseId} onBack={() => setCaseId(null)} />;
  return <CaseList onOpen={setCaseId} />;
}

function CaseList({ onOpen }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [queue, setQueue] = useState("general");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCases(await listMyCases());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    try {
      const created = await createCase({ subject, body, queue });
      toast.success("Case submitted");
      setSubject(""); setBody(""); setShowNew(false);
      load();
      onOpen(created.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="requester-support-list" className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-xl font-semibold text-slate-900">Help &amp; support</h1>
        <button onClick={() => setShowNew((v) => !v)} data-testid="requester-support-new-toggle" className="btn-brand px-4 py-1.5 text-sm">
          {showNew ? "Cancel" : "New request"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5 mb-6 space-y-3">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What do you need help with?"
                 data-testid="requester-support-subject-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <select value={queue} onChange={(e) => setQueue(e.target.value)} data-testid="requester-support-queue-select"
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm">
            {CASE_QUEUES.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
          </select>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Describe the issue…"
                    data-testid="requester-support-body-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <button type="submit" data-testid="requester-support-submit" className="btn-brand px-4 py-1.5 text-sm">Submit</button>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : cases.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No support requests yet.
        </div>
      ) : (
        <div className="space-y-2">
          {cases.map((c) => (
            <button key={c.id} onClick={() => onOpen(c.id)} data-testid={`requester-case-row-${c.id}`}
                    className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-500 transition flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{c.subject}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.case_reference}</div>
              </div>
              <span className="text-xs text-slate-600 whitespace-nowrap">{STATUS_LABEL[c.status] || c.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CaseThread({ caseId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [score, setScore] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getMyCase(caseId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await replyToMyCase(caseId, text);
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const rate = async (s) => {
    try {
      await submitCsat(caseId, s);
      toast.success("Thanks for the feedback!");
      setScore(s);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !data) return <div className="max-w-2xl mx-auto px-4 py-8 text-sm text-slate-500">Loading…</div>;
  const { case: kase, messages } = data;
  const canRate = ["resolved", "closed"].includes(kase.status) && !kase.csat_score;

  return (
    <div data-testid="requester-support-thread" className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={onBack} data-testid="requester-support-back" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
        <p className="text-xs text-slate-400 font-mono mb-1">{kase.case_reference}</p>
        <h1 className="font-display text-lg font-semibold text-slate-900">{kase.subject}</h1>
        <p className="text-xs text-slate-500 mt-1">{STATUS_LABEL[kase.status] || kase.status}</p>
      </div>

      <ul className="space-y-3 mb-4">
        {messages.map((m) => (
          <li key={m.id} className={`rounded-lg p-3 text-sm ${m.sender_type === "agent" ? "bg-emerald-50" : "bg-slate-50"}`}>
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span className="font-medium text-slate-700">{m.sender_type === "agent" ? "ConfirmPH Support" : "You"}</span>
              <span>{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p className="whitespace-pre-wrap text-slate-800">{m.text}</p>
          </li>
        ))}
      </ul>

      {kase.status !== "closed" && (
        <form onSubmit={send} className="flex gap-2 mb-6">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Write a reply…"
                    data-testid="requester-support-reply-textarea" className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2" />
          <button type="submit" data-testid="requester-support-reply-submit" className="btn-brand px-4 self-start">Send</button>
        </form>
      )}

      {canRate && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid="requester-support-csat">
          <p className="text-sm text-slate-600 mb-2">How did we do?</p>
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} onClick={() => rate(s)} data-testid={`requester-support-csat-${s}`}
                      className={`w-9 h-9 rounded-full border text-sm ${score >= s ? "bg-amber-400 border-amber-400 text-white" : "border-slate-300 text-slate-500"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
